import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Link,
} from '@mui/material';

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={1} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          PharmaMan Privacy Policy
        </Typography>

        <Typography variant="body1" paragraph>
          This Privacy Policy describes how your personal information is collected, used, and shared when you visit or make a purchase from Pharmaman.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
          1. Personal Information We Collect
        </Typography>
        
        <Typography variant="body1" paragraph>
          When you visit the Website, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies that are installed on your device. Additionally, as you browse the Website, we collect information about the individual web pages or products that you view, what websites or search terms referred you to the Website, and information about how you interact with the Website. We refer to this automatically-collected information as "Device Information".
        </Typography>
        
        <Typography variant="body1" paragraph>
          We collect Device Information using the following technologies:
        </Typography>
        
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Cookies:</strong> Data files that are placed on your device or computer and often include an anonymous unique identifier.
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Log files:</strong> Track actions occurring on the Website, and collect data including your IP address, browser type, Internet service provider, referring/exit pages, and date/time stamps.
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Web beacons:</strong> Electronic files used to record information about how you browse the Website.
          </Typography>
        </Box>
        
        <Typography variant="body1" paragraph>
          Additionally when you make a purchase or attempt to make a purchase through the Website, we collect certain information from you, including your name, billing address, shipping address, payment information, email address, and phone number. We refer to this information as "Order Information".
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
          2. How Do We Use Your Personal Information?
        </Typography>
        
        <Typography variant="body1" paragraph>
          We use the Order Information that we collect generally to fulfill any orders placed through the Website (including processing your payment information, arranging for shipping, and providing you with invoices and/or order confirmations). Additionally, we use this Order Information to:
        </Typography>
        
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li" variant="body1">Communicate with you;</Typography>
          <Typography component="li" variant="body1">Screen our orders for potential risk or fraud; and</Typography>
          <Typography component="li" variant="body1">When in line with the preferences you have shared with us, provide you with information or advertising relating to our products or services.</Typography>
        </Box>
        
        <Typography variant="body1" paragraph>
          We use the Device Information that we collect to help us screen for potential risk and fraud (in particular, your IP address), and more generally to improve and optimize our Website (for example, by generating analytics about how our customers browse and interact with the Website, and to assess the success of our marketing and advertising campaigns).
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
          3. Sharing Your Personal Information
        </Typography>
        
        <Typography variant="body1" paragraph>
          We share your Personal Information with third parties to help us use your Personal Information, as described above. For example, we use Shopify to power our online store--you can read more about how Shopify uses your Personal Information here: <Link href="https://www.shopify.com/legal/privacy" target="_blank" rel="noopener">https://www.shopify.com/legal/privacy</Link>. We also use Google Analytics to help us understand how our customers use the Website--you can read more about how Google uses your Personal Information here: <Link href="https://www.google.com/intl/en/policies/privacy/" target="_blank" rel="noopener">https://www.google.com/intl/en/policies/privacy/</Link>. You can also opt-out of Google Analytics here: <Link href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener">https://tools.google.com/dlpage/gaoptout</Link>.
        </Typography>
        
        <Typography variant="body1" paragraph>
          Finally, we may also share your Personal Information to comply with applicable laws and regulations, to respond to a subpoena, search warrant or other lawful request for information we receive, or to otherwise protect our rights.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
          4. Behavioral Advertising
        </Typography>
        
        <Typography variant="body1" paragraph>
          As described above, we use your Personal Information to provide you with targeted advertisements or marketing communications we believe may be of interest to you. For more information about how targeted advertising works, you can visit the Network Advertising Initiative's ("NAI") educational page at <Link href="http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work" target="_blank" rel="noopener">http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work</Link>.
        </Typography>
        
        <Typography variant="body1" paragraph>
          You can opt out of targeted advertising by using the links below:
        </Typography>
        
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li" variant="body1">
            <Link href="https://www.facebook.com/settings/?tab=ads" target="_blank" rel="noopener">Facebook</Link>
          </Typography>
          <Typography component="li" variant="body1">
            <Link href="https://www.google.com/settings/ads/anonymous" target="_blank" rel="noopener">Google</Link>
          </Typography>
          <Typography component="li" variant="body1">
            <Link href="https://advertise.bingads.microsoft.com/en-us/resources/policies/personalized-ads" target="_blank" rel="noopener">Bing</Link>
          </Typography>
        </Box>
        
        <Typography variant="body1" paragraph>
          Additionally, you can opt out of some of these services by visiting the Digital Advertising Alliance's opt-out portal at: <Link href="http://optout.aboutads.info/" target="_blank" rel="noopener">http://optout.aboutads.info/</Link>.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
          5. Your Rights
        </Typography>
        
        <Typography variant="body1" paragraph>
          If you are a European resident, you have the right to access personal information we hold about you and to ask that your personal information be corrected, updated, or deleted. If you would like to exercise this right, please contact us through the contact information below.
        </Typography>
        
        <Typography variant="body1" paragraph>
          Additionally, if you are a European resident we note that we are processing your information in order to fulfill contracts we might have with you (for example if you make an order through the Website), or otherwise to pursue our legitimate business interests listed above. Additionally, please note that your information will be transferred outside of Europe, including to Canada and the United States.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
          6. Data Retention
        </Typography>
        
        <Typography variant="body1" paragraph>
          When you place an order through the Website, we will maintain your Order Information for our records unless and until you ask us to delete this information.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
          7. Changes
        </Typography>
        
        <Typography variant="body1" paragraph>
          We may update this privacy policy from time to time in order to reflect, for example, changes to our practices or for other operational, legal, or regulatory reasons.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
          8. Contact Us
        </Typography>
        
        <Typography variant="body1" paragraph>
          For more information about our privacy practices, if you have questions, or if you would like to make a complaint, please contact us by e-mail at privacy@pharmaman.com or by mail using the details provided below:
        </Typography>
        
        <Typography variant="body1" paragraph>
          PharmaMan, 123 Health St, Wellness City, HC 45678
        </Typography>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;